/**
 * Fixed list of US states and Canadian provinces, keyed by country name, for state pickers,
 * address lines and location links.
 *
 * Consumers:
 * - `components/StockList/Purchase.tsx` builds the shipping-address state dropdown from
 *   `"United States"` only (the Stock List ships to US addresses only).
 * - `components/Shared/YourMetalExperts/YourMetalExperts.tsx` turns a Contentful location's
 *   `country` and full `state` name into the two-letter code, and into the link
 *   `/locations/<country, lower-cased and hyphenated>/<urlLabel>`.
 * - `components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company.tsx` → `formatState` turns
 *   a location's state name into its code, and assumes the match exists.
 *
 * Rules a change must keep:
 * - The country keys are matched against Contentful's location `country` values, and `label`
 *   against the location's `state`, by exact string. A spelling change here breaks those lookups.
 * - `urlLabel` must equal the slug that `pages/locations/united-states/[stateUrlLabel]/` and
 *   `pages/locations/canada/[provinceUrlLabel]/` build from Contentful
 *   (`@ryerson/frontend.contentful` → `getFlattenedLocationsHeaderData`). Those pages use
 *   `fallback: false`, so a link built from a different `urlLabel` is a 404.
 *
 * The US list is the 50 states, without DC or the territories. The Canada list has six
 * provinces (AB, BC, MB, NB, ON, QC). There is no Mexico entry: Mexico's location pages have no
 * state level. `src/unit-test/AboutUs/FoCCompany.spec.ts` mocks this module as `{ states: {} }`.
 */

/** One state or province. */
export interface StateAndProvince {
	/** Two-letter postal code, for example `"IL"` or `"ON"`. */
	id: string;
	/** URL slug, for example `"new-hampshire"`; see the file header for the pages it must match. */
	urlLabel: string;
	/**
	 * English display name, for example `"New Hampshire"`; matched against a Contentful
	 * location's `state`.
	 */
	label: string;
}

/** States and provinces by country name: `"United States"` and `"Canada"`. */
export const states: Record<string, StateAndProvince[]> = {
	"United States": [
		{
			id: "AL",
			urlLabel: "alabama",
			label: "Alabama",
		},
		{
			id: "AK",
			urlLabel: "alaska",
			label: "Alaska",
		},
		{
			id: "AZ",
			urlLabel: "arizona",
			label: "Arizona",
		},
		{
			id: "AR",
			urlLabel: "arkansas",
			label: "Arkansas",
		},
		{
			id: "CA",
			urlLabel: "california",
			label: "California",
		},
		{
			id: "CO",
			urlLabel: "colorado",
			label: "Colorado",
		},
		{
			id: "CT",
			urlLabel: "connecticut",
			label: "Connecticut",
		},
		{
			id: "DE",
			urlLabel: "delaware",
			label: "Delaware",
		},
		{
			id: "FL",
			urlLabel: "florida",
			label: "Florida",
		},
		{
			id: "GA",
			urlLabel: "georgia",
			label: "Georgia",
		},
		{
			id: "HI",
			urlLabel: "hawaii",
			label: "Hawaii",
		},
		{
			id: "ID",
			urlLabel: "idaho",
			label: "Idaho",
		},
		{
			id: "IL",
			urlLabel: "illinois",
			label: "Illinois",
		},
		{
			id: "IN",
			urlLabel: "indiana",
			label: "Indiana",
		},
		{
			id: "IA",
			urlLabel: "iowa",
			label: "Iowa",
		},
		{
			id: "KS",
			urlLabel: "kansas",
			label: "Kansas",
		},
		{
			id: "KY",
			urlLabel: "kentucky",
			label: "Kentucky",
		},
		{
			id: "LA",
			urlLabel: "louisiana",
			label: "Louisiana",
		},
		{
			id: "ME",
			urlLabel: "maine",
			label: "Maine",
		},
		{
			id: "MD",
			urlLabel: "maryland",
			label: "Maryland",
		},
		{
			id: "MA",
			urlLabel: "massachusetts",
			label: "Massachusetts",
		},
		{
			id: "MI",
			urlLabel: "michigan",
			label: "Michigan",
		},
		{
			id: "MN",
			urlLabel: "minnesota",
			label: "Minnesota",
		},
		{
			id: "MS",
			urlLabel: "mississippi",
			label: "Mississippi",
		},
		{
			id: "MO",
			urlLabel: "missouri",
			label: "Missouri",
		},
		{
			id: "MT",
			urlLabel: "montana",
			label: "Montana",
		},
		{
			id: "NE",
			urlLabel: "nebraska",
			label: "Nebraska",
		},
		{
			id: "NV",
			urlLabel: "nevada",
			label: "Nevada",
		},
		{
			id: "NH",
			urlLabel: "new-hampshire",
			label: "New Hampshire",
		},
		{
			id: "NJ",
			urlLabel: "new-jersey",
			label: "New Jersey",
		},
		{
			id: "NM",
			urlLabel: "new-mexico",
			label: "New Mexico",
		},
		{
			id: "NY",
			urlLabel: "new-york",
			label: "New York",
		},
		{
			id: "NC",
			urlLabel: "north-carolina",
			label: "North Carolina",
		},
		{
			id: "ND",
			urlLabel: "north-dakota",
			label: "North Dakota",
		},
		{
			id: "OH",
			urlLabel: "ohio",
			label: "Ohio",
		},
		{
			id: "OK",
			urlLabel: "oklahoma",
			label: "Oklahoma",
		},
		{
			id: "OR",
			urlLabel: "oregon",
			label: "Oregon",
		},
		{
			id: "PA",
			urlLabel: "pennsylvania",
			label: "Pennsylvania",
		},
		{
			id: "RI",
			urlLabel: "rhode-island",
			label: "Rhode Island",
		},
		{
			id: "SC",
			urlLabel: "south-carolina",
			label: "South Carolina",
		},
		{
			id: "SD",
			urlLabel: "south-dakota",
			label: "South Dakota",
		},
		{
			id: "TN",
			urlLabel: "tennessee",
			label: "Tennessee",
		},
		{
			id: "TX",
			urlLabel: "texas",
			label: "Texas",
		},
		{
			id: "UT",
			urlLabel: "utah",
			label: "Utah",
		},
		{
			id: "VT",
			urlLabel: "vermont",
			label: "Vermont",
		},
		{
			id: "VA",
			urlLabel: "virginia",
			label: "Virginia",
		},
		{
			id: "WA",
			urlLabel: "washington",
			label: "Washington",
		},
		{
			id: "WV",
			urlLabel: "west-virginia",
			label: "West Virginia",
		},
		{
			id: "WI",
			urlLabel: "wisconsin",
			label: "Wisconsin",
		},
		{
			id: "WY",
			urlLabel: "wyoming",
			label: "Wyoming",
		},
	],
	Canada: [
		{
			id: "AB",
			urlLabel: "alberta",
			label: "Alberta",
		},
		{
			id: "BC",
			urlLabel: "british-columbia",
			label: "British Columbia",
		},
		{
			id: "MB",
			urlLabel: "manitoba",
			label: "Manitoba",
		},
		{
			id: "NB",
			urlLabel: "new-brunswick",
			label: "New Brunswick",
		},
		{
			id: "ON",
			urlLabel: "ontario",
			label: "Ontario",
		},
		{
			id: "QC",
			urlLabel: "quebec",
			label: "Quebec",
		},
	],
};
